import React, { useState } from 'react';
import { X, AlertTriangle, Info, CheckCircle, Clock } from 'lucide-react';
import Portal from './UI/Portal';

const WithdrawalModal = ({ isOpen, onClose, currentStep, onConfirmWithdraw, workflow, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const getStepSpecificData = (step) => {
    const stepData = {
      1: {
        title: "Withdraw from Resignation",
        icon: Info,
        color: "blue",
        description: "Cancel the resignation process before any formal actions are taken.",
        consequences: [
          "Resignation record will be marked as withdrawn",
          "Any uploaded resignation letter will be archived",
          "No formal notifications will be sent",
          "Employee remains active in the system"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW RESIGNATION"
      },
      2: {
        title: "Withdraw from HOD Discussion",
        icon: AlertTriangle,
        color: "amber",
        description: "Cancel the offboarding process after HOD discussion but before end date establishment.",
        consequences: [
          "HOD discussion notes will be archived for reference",
          "Any agreed leave settlements will be voided",
          "Employee status remains unchanged",
          "Process will be reset to pre-discussion state"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW AFTER DISCUSSION"
      },
      3: {
        title: "Withdraw Resignation",
        icon: Clock,
        color: "orange",
        description: "Cancel the offboarding process after end date has been set.",
        consequences: [
          "Established end dates will be cleared",
          "Notice period calculations will be reset",
          "May affect payroll processing if already initiated",
          "Employee will be returned to active status"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW RESIGNATION"
      },
      4: {
        title: "Withdraw from Bond Assessment",
        icon: AlertTriangle,
        color: "red",
        description: "Cancel the offboarding process after bond assessment.",
        consequences: [
          "Bond assessment results will be archived",
          "Any breach calculations will be voided",
          "Finance team will be notified",
          "Bond obligations will be cancelled"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW BOND ASSESSMENT"
      },
      5: {
        title: "Withdraw from Final Settlement",
        icon: CheckCircle,
        color: "purple",
        description: "Cancel the offboarding process after final settlement calculations.",
        consequences: [
          "All settlement calculations will be voided",
          "Payroll processing will be halted",
          "Finance team will be notified",
          "Legal team will be consulted if needed"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW SETTLEMENT"
      },
      6: {
        title: "Withdraw from IR21 Processing",
        icon: AlertTriangle,
        color: "red",
        description: "Cancel the offboarding process during immigration processing.",
        consequences: [
          "IR21 processing will be terminated",
          "Immigration authorities will be notified",
          "Work permit status will be reviewed",
          "Immigration team will handle cancellation"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW IR21 PROCESS"
      },
      7: {
        title: "Withdraw from Final Exit",
        icon: CheckCircle,
        color: "green",
        description: "Cancel the offboarding process during final exit procedures.",
        consequences: [
          "All exit procedures will be reversed",
          "System access will need to be restored",
          "Asset return process will be cancelled",
          "IT and facilities teams will be notified"
        ],
        requiresApproval: false,
        confirmationText: "WITHDRAW FINAL EXIT"
      }
    };

    return stepData[step] || stepData[1];
  };

  const stepData = getStepSpecificData(currentStep);

  const handleSubmit = async () => {
    if (!reason) {
      alert('Please provide a reason for withdrawal');
      return;
    }

    if (confirmText !== stepData.confirmationText) {
      alert('Please type the confirmation text exactly as shown');
      return;
    }

    setIsSubmitting(true);

    try {
      await onConfirmWithdraw({
        step: currentStep,
        reason,
        additionalNotes,
        requiresApproval: stepData.requiresApproval,
        workflowId: workflow?.id
      }, onSuccess);

      onClose();
    } catch (error) {
      console.error('Withdrawal failed:', error);
      alert('Failed to process withdrawal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const Icon = stepData.icon;

  return (
    <Portal>
    <div className="modal-overlay">
      <div className="modal-container modal-container-lg">
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center">
            <div className={`p-3 rounded-full bg-${stepData.color}-100 mr-4`}>
              <Icon className={`w-6 h-6 text-${stepData.color}-600`} />
            </div>
            <div>
              <h2 className="modal-title">{stepData.title}</h2>
              {currentStep !== 3 && <p className="text-sm text-gray-600">Step {currentStep} of 7</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="modal-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
            <p className="text-gray-700">{stepData.description}</p>
          </div>

          {/* Consequences */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">What will happen:</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="space-y-2">
                {stepData.consequences.map((consequence, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span className="text-sm text-gray-700">{consequence}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Approval Notice */}
          {stepData.requiresApproval && (
            <div className={`bg-${stepData.color}-50 border border-${stepData.color}-200 rounded-lg p-4`}>
              <div className="flex items-center mb-2">
                <AlertTriangle className={`w-5 h-5 text-${stepData.color}-600 mr-2`} />
                <span className="font-medium text-gray-900">Approval Required</span>
              </div>
              <p className={`text-sm text-${stepData.color}-700`}>
                This withdrawal requires approval from relevant stakeholders before it can be processed.
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Withdrawal *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="select rounded-full"
            >
              <option value="">Select a reason...</option>
              <option value="employee_request">Employee changed their mind</option>
              <option value="management_decision">Management decision</option>
              <option value="contract_extension">Contract extension agreed</option>
              <option value="personal_circumstances">Personal circumstances</option>
              <option value="role_change">Internal role change</option>
              <option value="error_in_process">Error in resignation process</option>
              <option value="other">Other (specify below)</option>
            </select>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <input
              type="text"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              className="input rounded-full"
              placeholder="Any additional context or notes about this withdrawal..."
            />
          </div>

          {/* Confirmation */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              <span className="font-medium text-red-900">Final Confirmation</span>
            </div>
            <p className="text-sm text-red-700 mb-3">
              Type <strong>{stepData.confirmationText}</strong> to confirm withdrawal:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="input rounded-full"
              placeholder={stepData.confirmationText}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            onClick={onClose}
            className="btn btn-outline"
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <div className="flex space-x-3">
            {stepData.requiresApproval && (
              <div className="text-xs text-gray-500 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Approval workflow will be initiated
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!reason || confirmText !== stepData.confirmationText || isSubmitting}
              className="btn btn-danger"
            >
              {isSubmitting ? 'Processing...' : 'Confirm Withdrawal'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
};

export default WithdrawalModal;
